import React from 'react';
import { motion } from 'framer-motion';
import { getSymbolComponent } from './ZenerSymbols';

// Minimal Silver Holographic Foil Card Back - no pattern, performance friendly
const HolographicPattern = React.memo(() => {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-xl">
      {/* Silver metallic base */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #8e9299 0%, #c8ccd4 22%, #eef0f4 38%, #a8adb6 52%, #d9dce2 68%, #93979f 84%, #b9bdc6 100%)',
        }}
      />
      
      {/* Subtle iridescent foil tint */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(115deg, rgba(170,190,255,0.18) 0%, rgba(255,255,255,0) 30%, rgba(200,170,255,0.14) 50%, rgba(255,255,255,0) 70%, rgba(170,230,255,0.16) 100%)',
        }}
      />
      
      {/* Slow moving sheen - single GPU-friendly layer */}
      <motion.div
        className="absolute inset-y-0 w-1/2"
        style={{
          background: 'linear-gradient(105deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%)',
          mixBlendMode: 'overlay',
        }}
        animate={{ x: ['-120%', '320%'] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      
      {/* Soft vignette for depth */}
      <div
        className="absolute inset-0"
        style={{ boxShadow: 'inset 0 0 40px rgba(20,20,30,0.45)' }}
      />
    </div>
  );
});

export const ZenerCard = ({ symbol, isRevealed, isRevealing }) => {
  const SymbolComponent = getSymbolComponent(symbol);
  
  return (
    <div className="flip-card w-64 aspect-[2/3]" data-testid="zener-card">
      <motion.div
        className="flip-card-inner relative w-full h-full"
        animate={{ rotateY: isRevealed ? 180 : 0 }}
        transition={{ duration: 0.7, ease: 'easeInOut' }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front (face down) - Holographic Pattern */}
        <div
          className="flip-card-front absolute inset-0 rounded-xl border border-white/30 shadow-2xl shadow-slate-400/20 overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <HolographicPattern />
        </div>
        
        {/* Back (revealed symbol) */}
        <div
          className="flip-card-back absolute inset-0 rounded-xl border border-violet-500/50 bg-gradient-to-br from-[#0f0f10] to-[#1a1a1f] shadow-2xl shadow-violet-500/20 flex items-center justify-center"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="w-32 h-32 text-violet-400" data-testid={`revealed-symbol-${symbol}`}>
            <SymbolComponent />
          </div>
        </div>
      </motion.div>
    </div>
  );
};
